import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon, TrendingUpIcon, FileTextIcon, CalendarIcon, MessageSquareIcon, BarChart3Icon, HomeIcon, BrainIcon, ZapIcon, ShieldCheckIcon, PlayCircleIcon } from 'lucide-react';
import VeriScoreCard from '../components/VeriBot/VeriScoreCard';
import VeriBotDemo from '../components/VeriBot/VeriBotDemo';
const VeriBotPage = () => {
  const [activeDemo, setActiveDemo] = useState<string | null>(null);
  const [showDemoVideo, setShowDemoVideo] = useState(false);
  const features = [{
    icon: <BrainIcon className="w-6 h-6" data-id="element-1889" />,
    title: 'AI Property Scouting',
    description: 'VeriBot analyzes historical data and infrastructure plans to provide VeriScore ratings for every property.',
    color: 'from-purple-500 to-indigo-600'
  }, {
    icon: <FileTextIcon className="w-6 h-6" data-id="element-1890" />,
    title: 'Real-time Document Tracking',
    description: 'Monitor document validation status with live updates and instant notifications.',
    color: 'from-blue-500 to-cyan-600'
  }, {
    icon: <HomeIcon className="w-6 h-6" data-id="element-1891" />,
    title: '3D Virtual Tours',
    description: 'Experience properties through immersive 3D video tours powered by AI.',
    color: 'from-green-500 to-emerald-600'
  }, {
    icon: <MessageSquareIcon className="w-6 h-6" data-id="element-1892" />,
    title: '24/7 AI Assistant',
    description: 'Get instant answers, insights, and task management through conversational AI.',
    color: 'from-orange-500 to-red-600'
  }];
  const capabilities = [{
    category: 'Investment Analysis',
    items: ['Calculate ROI and rental yields', 'Predict property appreciation', 'Compare market trends', 'Identify emerging hotspots']
  }, {
    category: 'Property Management',
    items: ['Track payment status', 'Monitor document verification', 'Schedule property services', 'Manage maintenance requests']
  }, {
    category: 'Smart Insights',
    items: ['Infrastructure development alerts', 'Price trend predictions', 'Neighborhood analysis', 'Investment recommendations']
  }];
  const exampleQueries = [{
    query: "What's the VeriScore for properties in Lekki?",
    category: 'Investment'
  }, {
    query: 'Show me the status of my document verification',
    category: 'Tracking'
  }, {
    query: 'Schedule a deep cleaning for next Saturday',
    category: 'Services'
  }, {
    query: 'What are the trending areas for investment?',
    category: 'Analysis'
  }, {
    query: 'Calculate rental yield for my Abuja property',
    category: 'Investment'
  }, {
    query: 'Track my key delivery',
    category: 'Tracking'
  }];
  return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100" data-id="element-1893">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#1A1F47] via-[#2A2F57] to-[#1A1F47]" data-id="element-1894">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE2YzAtMi4yMSAxLjc5LTQgNC00czQgMS43OSA0IDQtMS43OSA0LTQgNC00LTEuNzktNC00em0wIDI0YzAtMi4yMSAxLjc5LTQgNC00czQgMS43OSA0IDQtMS43OSA0LTQgNC00LTEuNzktNC00ek0xMiAxNmMwLTIuMjEgMS43OS00IDQtNHM0IDEuNzkgNCA0LTEuNzkgNC00IDQtNC0xLjc5LTQtNHptMCAyNGMwLTIuMjEgMS43OS00IDQtNHM0IDEuNzkgNCA0LTEuNzkgNC00IDQtNC0xLjc5LTQtNHoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-20" data-id="element-1895"></div>

        <div className="container mx-auto px-4 py-20 relative z-10" data-id="element-1896">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-center max-w-4xl mx-auto" data-id="element-1897">
            <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-6 py-3 mb-8" data-id="element-1898">
              <SparklesIcon className="w-5 h-5 text-[#FF4E00]" data-id="element-1899" />
              <span className="text-white font-medium" data-id="element-1900">
                Powered by Advanced AI
              </span>
            </motion.div>

            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6" data-id="element-1901">
              Meet VeriBot
              <br data-id="element-1902" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF4E00] to-[#FF6B2C]" data-id="element-1903">
                Your AI Property Assistant
              </span>
            </h1>

            <p className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto" data-id="element-1904">
              24/7 intelligent support for property scouting, investment
              analysis, document tracking, and service management. Your personal
              gateway to seamless property ownership.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4" data-id="element-1905">
              <motion.button whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} className="px-8 py-4 bg-gradient-to-r from-[#FF4E00] to-[#FF6B2C] text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition" data-id="element-1906">
                Try VeriBot Now
              </motion.button>
              <motion.button whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} onClick={() => setShowDemoVideo(true)} className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-xl border border-white/20 hover:bg-white/20 transition flex items-center space-x-2" data-id="element-1907">
                <PlayCircleIcon className="w-5 h-5" data-id="element-1908" />
                <span data-id="element-1909">Watch Demo</span>
              </motion.button>
            </div>
          </motion.div>
        </div>

        {/* Floating Elements */}
        <motion.div animate={{
        y: [0, -20, 0],
        rotate: [0, 5, 0]
      }} transition={{
        duration: 6,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute top-20 right-10 w-32 h-32 bg-gradient-to-br from-[#FF4E00]/20 to-[#FF6B2C]/20 rounded-3xl backdrop-blur-sm hidden lg:block" data-id="element-1910" />
        <motion.div animate={{
        y: [0, 20, 0],
        rotate: [0, -5, 0]
      }} transition={{
        duration: 8,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute bottom-20 left-10 w-40 h-40 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-3xl backdrop-blur-sm hidden lg:block" data-id="element-1911" />
      </div>

      {/* Features Grid */}
      <div className="container mx-auto px-4 py-20" data-id="element-1912">
        <div className="text-center mb-16" data-id="element-1913">
          <h2 className="text-4xl font-bold text-gray-800 mb-4" data-id="element-1914">
            AI-Powered Capabilities
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-1915">
            VeriBot combines advanced AI with real-time data to revolutionize
            your property journey
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20" data-id="element-1916">
          {features.map((feature, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition group" data-id="element-1917">
              <div className={`w-14 h-14 bg-gradient-to-br ${feature.color} rounded-xl flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform`} data-id="element-1918">
                {feature.icon}
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-1919">
                {feature.title}
              </h3>
              <p className="text-gray-600" data-id="element-1920">{feature.description}</p>
            </motion.div>)}
        </div>

        {/* VeriScore Demo */}
        <div className="mb-20" data-id="element-1921">
          <div className="text-center mb-12" data-id="element-1922">
            <h2 className="text-4xl font-bold text-gray-800 mb-4" data-id="element-1923">
              VeriScore: AI Investment Analysis
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-1924">
              Get instant ROI predictions and investment insights powered by
              machine learning
            </p>
          </div>

          <div className="max-w-2xl mx-auto" data-id="element-1925">
            <VeriScoreCard score={8.7} propertyName="Luxury 3 Bedroom Apartment" location="Lekki Phase 1, Lagos" insights={{
            rentalYield: 6.2,
            appreciation: 12,
            infrastructureScore: 9,
            demandLevel: 'High'
          }} trends={[{
            label: 'New Lekki-Epe Expressway',
            value: 'Completed 2024',
            positive: true
          }, {
            label: 'Shopping Complex Development',
            value: 'Opening Q2 2024',
            positive: true
          }, {
            label: 'Rental Demand',
            value: '+18% YoY',
            positive: true
          }, {
            label: 'Property Prices',
            value: '+12% (3yr avg)',
            positive: true
          }]} data-id="element-1926" />
          </div>
        </div>

        {/* Capabilities Section */}
        <div className="mb-20" data-id="element-1927">
          <div className="text-center mb-12" data-id="element-1928">
            <h2 className="text-4xl font-bold text-gray-800 mb-4" data-id="element-1929">
              What VeriBot Can Do
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-1930">
              Your AI assistant handles everything from analysis to execution
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1931">
            {capabilities.map((capability, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white rounded-2xl p-8 shadow-lg" data-id="element-1932">
                <h3 className="text-2xl font-bold text-gray-800 mb-6" data-id="element-1933">
                  {capability.category}
                </h3>
                <ul className="space-y-4" data-id="element-1934">
                  {capability.items.map((item, idx) => <li key={idx} className="flex items-start space-x-3" data-id="element-1935">
                      <div className="w-6 h-6 bg-[#FFF1EC] rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-1936">
                        <ZapIcon className="w-4 h-4 text-[#FF4E00]" data-id="element-1937" />
                      </div>
                      <span className="text-gray-700" data-id="element-1938">{item}</span>
                    </li>)}
                </ul>
              </motion.div>)}
          </div>
        </div>

        {/* Example Queries */}
        <div className="mb-20" data-id="element-1939">
          <div className="text-center mb-12" data-id="element-1940">
            <h2 className="text-4xl font-bold text-gray-800 mb-4" data-id="element-1941">
              Try These Questions
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-1942">
              VeriBot understands natural language and provides instant,
              intelligent responses
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto" data-id="element-1943">
            {exampleQueries.map((example, index) => <motion.button key={index} initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} transition={{
            delay: index * 0.05
          }} className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg transition text-left group" data-id="element-1944">
                <div className="flex items-start justify-between mb-2" data-id="element-1945">
                  <span className="text-xs font-semibold text-[#FF4E00] bg-[#FFF1EC] px-2 py-1 rounded" data-id="element-1946">
                    {example.category}
                  </span>
                  <MessageSquareIcon className="w-4 h-4 text-gray-400 group-hover:text-[#FF4E00] transition" data-id="element-1947" />
                </div>
                <p className="text-gray-700 font-medium" data-id="element-1948">{example.query}</p>
              </motion.button>)}
          </div>
        </div>

        {/* CTA Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-gradient-to-r from-[#FF4E00] to-[#FF6B2C] rounded-3xl p-12 text-center text-white" data-id="element-1949">
          <SparklesIcon className="w-16 h-16 mx-auto mb-6" data-id="element-1950" />
          <h2 className="text-4xl font-bold mb-4" data-id="element-1951">
            Ready to Experience AI-Powered Property Management?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto" data-id="element-1952">
            VeriBot is available 24/7 to help you make smarter property
            decisions. Start chatting now.
          </p>
          <motion.button whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} className="px-8 py-4 bg-white text-[#FF4E00] font-bold rounded-xl shadow-lg hover:shadow-xl transition" data-id="element-1953">
            Launch VeriBot
          </motion.button>
        </motion.div>
      </div>

      {/* Demo Video Modal */}
      <VeriBotDemo isOpen={showDemoVideo} onClose={() => setShowDemoVideo(false)} data-id="element-1954" />
    </div>;
};
export default VeriBotPage;